import { Fragment } from "react";
import Markdown, { type MarkdownToJSX } from "markdown-to-jsx";
import { cn } from "@/lib/utils";

interface AssistantMarkdownProps {
  children: string;
  className?: string;
}

// The webapp has no typography/prose plugin, so each element is styled explicitly via overrides.
// Spacing is tuned for the chat bubble: tight vertical rhythm, theme tokens only.
const markdownOptions: MarkdownToJSX.Options = {
  // No raw HTML: model output renders as markdown only, never as injected markup.
  disableParsingRawHTML: true,
  // Our outer div owns the vertical spacing; keep block children as direct children of it.
  wrapper: Fragment,
  forceWrapper: true,
  overrides: {
    p: { props: { className: "whitespace-pre-wrap" } },
    strong: { props: { className: "font-semibold" } },
    em: { props: { className: "italic" } },
    ul: { props: { className: "list-disc space-y-1 pl-5" } },
    ol: { props: { className: "list-decimal space-y-1 pl-5" } },
    li: { props: { className: "whitespace-pre-wrap" } },
    a: { props: { target: "_blank", rel: "noopener noreferrer", className: "text-primary underline underline-offset-2" } },
    code: { props: { className: "rounded bg-muted px-1 py-0.5 font-mono text-[0.85em]" } },
    pre: {
      props: {
        className: "overflow-x-auto rounded-md bg-muted p-3 text-[0.85em] [&>code]:bg-transparent [&>code]:p-0",
      },
    },
    h1: { props: { className: "text-base font-semibold" } },
    h2: { props: { className: "text-sm font-semibold" } },
    h3: { props: { className: "text-sm font-semibold" } },
    blockquote: { props: { className: "border-l-2 border-border pl-3 text-muted-foreground" } },
    hr: { props: { className: "border-border" } },
    table: { props: { className: "w-full border-collapse text-left" } },
    th: { props: { className: "border border-border px-2 py-1 font-semibold" } },
    td: { props: { className: "border border-border px-2 py-1 align-top" } },
  },
};

/**
 * Renders an assistant reply as Markdown. Used for both persisted and streaming turns so the two paths
 * look identical. Raw HTML parsing is off, so model output can't inject markup.
 */
export function AssistantMarkdown({ children, className }: AssistantMarkdownProps) {
  return (
    <div className={cn("space-y-2 text-sm text-foreground break-words", className)}>
      <Markdown options={markdownOptions}>{children}</Markdown>
    </div>
  );
}
